import type { ComponentType, ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../utils/cn';

export type SectionTone = 'default' | 'watch' | 'poor' | 'good';

interface StaffSectionProps {
  title: string;
  icon: ComponentType<{ className?: string }>;
  count?: number;
  tone?: SectionTone;
  /** Right-aligned slot in the header (filters, toggles). */
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

const ICON_TILE: Record<SectionTone, string> = {
  default: 'bg-accent-500/15 text-accent-300',
  good: 'bg-green-500/15 text-green-300',
  watch: 'bg-yellow-500/15 text-yellow-300',
  poor: 'bg-red-500/15 text-red-300',
};

const HAIRLINE: Record<SectionTone, string> = {
  default: 'via-accent-400/50',
  good: 'via-green-400/50',
  watch: 'via-yellow-400/50',
  poor: 'via-red-400/50',
};

/**
 * Glass section card with a gradient hairline, an icon tile, an eyebrow title and an
 * optional count. Fades up on mount (disabled under reduced motion).
 */
export const StaffSection = ({ title, icon: Icon, count, tone = 'default', actions, className, bodyClassName, children }: StaffSectionProps) => {
  const reduce = useReducedMotion();
  return (
    <motion.section
      initial={reduce ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className={cn('glass-card relative overflow-hidden transition-colors hover:border-accent-400/30', className)}
    >
      <div className={cn('absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent', HAIRLINE[tone])} aria-hidden="true" />
      <header className="flex flex-wrap items-center gap-3 px-4 pt-4 md:px-5 md:pt-5">
        <span className={cn('inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0', ICON_TILE[tone])}>
          <Icon className="w-4 h-4" />
        </span>
        <h2 className="text-sm font-semibold text-white tracking-wide">
          {title}
          {typeof count === 'number' && (
            <span className="ml-2 text-xs font-mono font-normal text-glass-400 tabular-nums">{count}</span>
          )}
        </h2>
        {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      <div className={cn('px-4 pb-4 pt-3 md:px-5 md:pb-5', bodyClassName)}>{children}</div>
    </motion.section>
  );
};
